import { useState, useEffect, useMemo } from "react";
import RequiredMark from "../../../../new_components/common/required_mark";

interface TypeableSelectFieldProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  id?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  showValidationError?: boolean;
  customErrorMessage?: string;
  onClear?: () => void;
  onValidSelection?: (value: string) => void;
}

const ComplaintRelatedToSearchField: React.FC<TypeableSelectFieldProps> = ({
  label,
  value,
  options = [],
  onChange,
  id,
  name,
  disabled = false,
  required = false,
  className = "",
  showValidationError = true,
  customErrorMessage,
  onClear,
  onValidSelection,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    if (value && !isTyping) {
      setSearchValue(value);
    } else if (!value && !isTyping) {
      setSearchValue("");
    }
  }, [value, isTyping]);

  const filteredSuggestions = useMemo(() => {
    if (!searchValue.trim()) {
      return options.map((option) => ({
        label: option,
        value: option,
      }));
    }

    return options
      .filter((option) =>
        option.toLowerCase().includes(searchValue.toLowerCase())
      )
      .map((option) => ({
        label: option,
        value: option,
      }));
  }, [searchValue, options]);

  const isValidSelection = options.includes(value);
  const hasValue = value && value.trim() !== "";

  const handleInputChange = (inputValue: string) => {
    setSearchValue(inputValue);
    setIsTyping(true);
    setShowSuggestions(true);
    setHasInteracted(true);

    if (inputValue !== value) {
      onChange("");
      if (onClear) {
        onClear();
      }
    }
  };

  const handleSuggestionSelect = (suggestion: {
    label: string;
    value: string;
  }) => {
    setSearchValue(suggestion.value);
    setShowSuggestions(false);
    setIsTyping(false);
    setHasInteracted(true);

    onChange(suggestion.value);

    if (onValidSelection) {
      onValidSelection(suggestion.value);
    }
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setShowSuggestions(true);
      setHasInteracted(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      setIsTyping(false);

      if (searchValue && !options.includes(searchValue)) {
        setSearchValue(value || "");
      }
    }, 200);
  };

  const getErrorMessage = () => {
    if (customErrorMessage) {
      return customErrorMessage;
    }

    if (hasValue && !isValidSelection) {
      return `Please select a valid option from the list`;
    }

    if (required && hasInteracted && !hasValue) {
      return `${label} is required`;
    }

    return null;
  };

  const errorMessage = getErrorMessage();
  const shouldShowError = showValidationError && errorMessage;

  const getPlaceholder = () => {
    if (disabled && options.length === 0) {
      return "--";
    }
    return "Type to search...";
  };

  return (
    <div className={`flex flex-col ${className}`}>
      <label htmlFor={id} className="mr-2 text-sm leading-7">
        {label} {required && <RequiredMark />}
      </label>

      <div className="relative w-full">
        <input
          disabled={disabled}
          required={required}
          autoComplete="off"
          id={id}
          name={name}
          value={searchValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className={`text-xs px-2 py-2 h-full focus:outline-none w-full border ${
            shouldShowError ? "border-red-400" : ""
          }`}
          type="text"
          placeholder={getPlaceholder()}
        />

        {/* Suggestions Dropdown */}
        {showSuggestions && !disabled && options.length > 0 && (
          <div
            id={id && `${id}-suggestions`}
            className="flex flex-col border rounded absolute bg-white z-40 w-full mt-1 shadow-lg"
          >
            <div className="flex flex-col w-full max-h-72 overflow-y-auto overflow-x-hidden divide-y">
              {filteredSuggestions.length > 0 ? (
                filteredSuggestions.map((suggestion, index) => (
                  <div
                    id={id && `${id}-option-${index}`}
                    className="text-sm hover:bg-gray-100 px-2 py-2 cursor-pointer"
                    key={index}
                    onMouseDown={() => handleSuggestionSelect(suggestion)}
                  >
                    {suggestion.label}
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500 px-2 py-2">
                  No matching options found
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {shouldShowError && (
        <p className="text-xs text-red-400 mt-1">{errorMessage}</p>
      )}

      {/* Show typing feedback if user is searching but hasn't selected */}
      {searchValue && !value && isTyping && !shouldShowError && (
        <p className="text-xs text-orange-400 mt-1">
          Please select an option from the dropdown
        </p>
      )}
    </div>
  );
};

export default ComplaintRelatedToSearchField;
